import { MRT_STATIONS, SCHOOLS } from "@/lib/hubPages";

// Place lookups for the "near" search. This module reads only the hub
// lists (labels, slugs, districts) so the homepage bundle stays light.
// Coordinates and distance live in nearSearch.ts.

export type NearKind = "mrt" | "school";

export type NearPlace = {
  kind: NearKind;
  slug: string;
  label: string;
  districts: string[];
  lat?: number;
  lng?: number;
};

export function parseNear(near: string): { kind: NearKind; slug: string } | null {
  const m = /^(mrt|school):([a-z0-9-]+)$/i.exec(near.trim());
  if (!m) return null;
  return { kind: m[1].toLowerCase() as NearKind, slug: m[2].toLowerCase() };
}

export function nearKey(place: Pick<NearPlace, "kind" | "slug">): string {
  return `${place.kind}:${place.slug}`;
}

/** Display name: stations get an "MRT" suffix, schools use their label. */
export function nearLabel(place: Pick<NearPlace, "kind" | "label">): string {
  return place.kind === "mrt" ? `${place.label} MRT` : place.label;
}

/** Resolve a `near` value to its hub entry, without coordinates. */
export function findPlace(near: string): NearPlace | null {
  const parsed = parseNear(near);
  if (!parsed) return null;
  if (parsed.kind === "mrt") {
    const s = MRT_STATIONS.find((x) => x.slug === parsed.slug);
    return s ? { kind: "mrt", slug: s.slug, label: s.label, districts: s.districts } : null;
  }
  const s = SCHOOLS.find((x) => x.slug === parsed.slug);
  return s ? { kind: "school", slug: s.slug, label: s.label, districts: s.districts } : null;
}

/** Chip label for a `near` value, or "" when it does not resolve. */
export function describeNear(near: string): string {
  const place = findPlace(near);
  return place ? nearLabel(place) : "";
}

function norm(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

/** Suggestion rows for the search dropdown: label contains the query. */
export function matchPlaces(query: string, limit = 4): { mrt: NearPlace[]; schools: NearPlace[] } {
  const q = norm(query);
  if (q.length < 2) return { mrt: [], schools: [] };
  const rank = (label: string) => (norm(label).startsWith(q) ? 0 : 1);
  const mrt = MRT_STATIONS.filter((s) => norm(s.label).includes(q))
    .sort((a, b) => rank(a.label) - rank(b.label) || a.label.localeCompare(b.label))
    .slice(0, limit)
    .map<NearPlace>((s) => ({ kind: "mrt", slug: s.slug, label: s.label, districts: s.districts }));
  const schools = SCHOOLS.filter((s) => norm(s.label).includes(q))
    .sort((a, b) => rank(a.label) - rank(b.label) || a.label.localeCompare(b.label))
    .slice(0, limit)
    .map<NearPlace>((s) => ({ kind: "school", slug: s.slug, label: s.label, districts: s.districts }));
  return { mrt, schools };
}

/**
 * Free-text fallback: a query that equals a station or school label
 * ("hougang", "hougang mrt", "rosyth school") resolves to that place.
 */
export function findPlaceByText(query: string): NearPlace | null {
  const raw = norm(query);
  if (raw.length < 3) return null;
  const stationQ = raw.replace(/\b(mrt|station)\b/g, "").trim();
  const station = MRT_STATIONS.find((s) => norm(s.label) === stationQ);
  if (station) return { kind: "mrt", slug: station.slug, label: station.label, districts: station.districts };
  const school =
    SCHOOLS.find((s) => norm(s.label) === raw) ??
    SCHOOLS.find((s) => norm(s.label).replace(/\b(primary|school)\b/g, "").trim() === raw.replace(/\b(primary|school)\b/g, "").trim());
  if (school) return { kind: "school", slug: school.slug, label: school.label, districts: school.districts };
  return null;
}
